<script setup>
import { onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Badge, Button, Card, Textarea } from '@drama/ui'

import { api, getToken } from '../api.js'
import ListToolbar from '../components/ListToolbar.vue'
import { useListView } from '../list-view.js'

const { t } = useI18n()
const router = useRouter()
const requests = ref(null)
const drafts = reactive({})
const error = ref('')
const view = useListView(requests, {
  text: (request) => `${request.agent_name}\n${request.task_title}\n${request.question}`,
  filters: [{ key: 'status', label: t('list.all'), options: ['open', 'answered'].map((value) => ({ value, label: t(`help.status.${value}`) })), match: (request, value) => request.status === value }],
  sorts: [{ value: 'newest', label: t('channels.sortNewest'), compare: (a, b) => b.created_at.localeCompare(a.created_at) }, { value: 'oldest', label: t('list.oldest'), compare: (a, b) => a.created_at.localeCompare(b.created_at) }],
})

async function load() {
  requests.value = (await api('GET', '/api/voko/help')).help_requests
}

/** @param {string} id */
async function answer(id) {
  error.value = ''
  try {
    await api('POST', `/api/voko/help/${id}/answer`, { answer: drafts[id] })
    await load()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

onMounted(async () => {
  if (getToken() === null) {
    await router.push({ path: '/login', query: { next: '/help' } })
    return
  }
  await load()
})
</script>

<template>
  <h1>{{ t('help.title') }}</h1>
  <p><router-link to="/agents">{{ t('board.back') }}</router-link></p>
  <p v-if="error" role="alert">{{ error }}</p>
  <template v-if="requests">
    <p v-if="requests.length === 0">{{ t('help.empty') }}</p>
    <ListToolbar v-if="requests.length > 0" :view="view" />
    <p v-if="requests.length > 0 && view.shown.value.length === 0">{{ t('list.noMatch') }}</p>
    <Card v-for="request in view.shown.value" :key="request.id" class="voko-help">
      <h2>{{ request.agent_name }} <Badge :variant="request.status === 'open' ? 'warning' : 'success'">{{ t(`help.status.${request.status}`) }}</Badge> <Badge :variant="request.kind === 'authority' ? 'warning' : 'info'">{{ t(`help.kind.${request.kind}`) }}</Badge></h2>
      <p>{{ t('help.task', { title: request.task_title }) }} · {{ new Date(request.created_at).toLocaleString() }}</p>
      <p class="voko-pre">{{ request.question }}</p>
      <template v-if="request.status === 'open'">
        <label class="voko-field">{{ t('help.answer') }}<Textarea v-model="drafts[request.id]" :rows="3" /></label>
        <Button :disabled="!drafts[request.id] || drafts[request.id].trim() === ''" @click="answer(request.id)">{{ t('help.send') }}</Button>
      </template>
      <p v-else class="voko-pre"><strong>{{ t('help.yourAnswer') }}</strong> {{ request.answer }}</p>
    </Card>
  </template>
</template>

<style scoped>
.voko-help {
  margin-bottom: var(--drama-space-md);
}
.voko-pre {
  white-space: pre-wrap;
}
</style>
